import { useEffect, useMemo, useRef, useState } from 'react';
import { Field } from './ui.jsx';
import { IconPlus } from './Icons.jsx';
import { t } from '../lib/i18n.js';

/**
 * Type-ahead over an already-loaded list, with the option to create the record
 * inline. At the gate a supplier or customer often turns up who is not on file
 * yet, and stopping to open another screen loses the entry.
 *
 * `onChange` reports `{ id, name }`. A newly typed name comes back with
 * `id: null`, and the API creates it when the form is submitted.
 */
export function EntityPicker({
  label,
  hint,
  error,
  required,
  items = [],
  value,
  name,
  onChange,
  allowCreate = true,
  placeholder = t.shared.searchOrType,
  createLabel = t.common.add,
  disabled,
  className,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrap = useRef(null);

  const selected = useMemo(() => items.find((i) => i.id === value), [items, value]);
  const displayText = open ? query : selected?.name || name || '';

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? items.filter(
          (i) =>
            i.name?.toLowerCase().includes(q) ||
            i.phone?.toLowerCase().includes(q) ||
            (i.plates || []).some((p) => p.toLowerCase().includes(q)),
        )
      : items;
    return list.slice(0, 40);
  }, [items, query]);

  const exactMatch = matches.some((i) => i.name?.toLowerCase() === query.trim().toLowerCase());
  const showCreate = allowCreate && query.trim().length > 0 && !exactMatch;

  useEffect(() => {
    const onDocumentDown = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentDown);
    return () => document.removeEventListener('mousedown', onDocumentDown);
  }, []);

  const choose = (item) => {
    onChange({ id: item.id, name: item.name });
    setQuery('');
    setOpen(false);
  };

  const create = () => {
    onChange({ id: null, name: query.trim() });
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    const optionCount = matches.length + (showCreate ? 1 : 0);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, optionCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight < matches.length) choose(matches[highlight]);
      else if (showCreate) create();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, ...a11y }) => (
        <div className="picker" ref={wrap}>
          <input
            id={id}
            {...a11y}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder}
            value={displayText}
            onFocus={() => {
              setQuery('');
              setHighlight(0);
              setOpen(true);
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
              setOpen(true);
            }}
            onKeyDown={onKeyDown}
          />
          {open && (matches.length > 0 || showCreate) && (
            <div className="picker-list" role="listbox">
              {matches.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={item.id === value}
                  className={`picker-option ${index === highlight ? 'highlight' : ''}`}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => choose(item)}
                >
                  <span className="truncate">{item.name}</span>
                  {(item.phone || item.plates?.length > 0) && (
                    <span className="meta">{item.phone || item.plates.join(', ')}</span>
                  )}
                </button>
              ))}
              {showCreate && (
                <button
                  type="button"
                  className={`picker-option create ${highlight === matches.length ? 'highlight' : ''}`}
                  onMouseEnter={() => setHighlight(matches.length)}
                  onClick={create}
                >
                  <IconPlus />
                  <span>
                    {createLabel} &ldquo;{query.trim()}&rdquo;
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </Field>
  );
}
